/**
 * Auditoria — trilha de mutações (quando/entidade/ação/antes→depois), padrão Mantine.
 */
import { useEffect, useState } from "react";
import { Alert, Code, Select, Table, Text, Title } from "@mantine/core";
import { api } from "../api";
import { fmtDataHora } from "../format";
import { Carregando, Vazio } from "../componentes/Estados";
import { TabelaDados, Th } from "../componentes/listas";

interface Registro {
  id: string;
  timestamp: string;
  entidade: string;
  entidadeId: string;
  acao: string;
  antes: string | null;
  depois: string | null;
  descricao: string;
  quem: string | null;
}

const ENTIDADES = [
  "funcionarios",
  "contratos",
  "parametros",
  "formulas",
  "eventos",
  "competencias",
  "divergencias",
  "ponto_importacoes",
  "ponto_depara",
  "seed",
];

export function AuditoriaPage() {
  const [lista, setLista] = useState<Registro[] | null>(null);
  const [entidade, setEntidade] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    api
      .get<{ auditoria: Registro[] }>(`/auditoria${entidade ? `?entidade=${entidade}` : ""}`)
      .then((r) => setLista(r.auditoria))
      .catch(setErro);
  }, [entidade]);

  return (
    <>
      <Title order={2} mb={4}>Auditoria</Title>
      <Text size="sm" c="dimmed" mb="md">
        Trilha de todas as mutações do sistema (antes → depois, com motivo). O "quem" fica vazio no MVP — decisão do dono.
      </Text>
      <Select
        placeholder="todas as entidades"
        clearable
        data={ENTIDADES}
        value={entidade}
        onChange={setEntidade}
        w={280}
        mb="sm"
        aria-label="Filtrar por entidade"
      />
      {erro != null && (
        <Alert color="red" title="Falha ao carregar" mb="sm">
          {String(erro)}
        </Alert>
      )}
      {!lista ? (
        <Carregando linhas={6} />
      ) : lista.length === 0 ? (
        <Vazio texto="Nenhum registro de auditoria com esse filtro." />
      ) : (
        <TabelaDados>
          <Table.Thead>
            <Table.Tr>
              <Th>Quando</Th>
              <Th>Entidade</Th>
              <Th>Ação</Th>
              <Th>Descrição</Th>
              <Th>Antes → Depois</Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lista.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>
                  <Text size="sm">{fmtDataHora(r.timestamp)}</Text>
                </Table.Td>
                <Table.Td>
                  <Code>{r.entidade}/{r.entidadeId.slice(0, 10)}</Code>
                </Table.Td>
                <Table.Td>{r.acao}</Table.Td>
                <Table.Td>
                  <Text size="sm">{r.descricao}</Text>
                </Table.Td>
                <Table.Td>
                  {(r.antes || r.depois) && (
                    <details>
                      <summary>
                        <Text span size="xs" c="dimmed">ver</Text>
                      </summary>
                      {r.antes && <Code block>antes: {r.antes}</Code>}
                      {r.depois && <Code block>depois: {r.depois}</Code>}
                    </details>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </TabelaDados>
      )}
      <Text size="xs" c="dimmed" mt="sm">
        O campo "quem" fica vazio no MVP (sem controle de usuários — decisão do dono; aditivo no futuro).
      </Text>
    </>
  );
}
